/**
 * 詳情頁「可預約時段」日曆的資料層（AV P6）。
 *
 * 命題是「場主公告的可預約時段是哪些」，不是「這個場地有空嗎」——後者要完整占用資料，
 * prod 上只有 6.7% 的場地有；前者只要販售時段，253 個全都有。運算全部留在後端
 * （`micrositeslots`），這裡只把 `/api/space/schedule` 的四態翻成看得懂的字與圖示。
 *
 * ⚠️ **不得從 `slots.length` 推狀態**：`closed`（那天沒開賣）與 `full`（開賣但被訂光）的
 * `slots` 都是空的，只有 `state` 分得開，而兩者要對租客說的話完全不同——把 `closed`
 * 說成「已無時段」，等於替場主宣告一件我們並不知道的事。
 */

/** 對應後端 `viewmodels.SpaceScheduleState*`。 */
export type ScheduleState = 'open' | 'partial' | 'full' | 'closed'

export interface ScheduleSlot {
    start: string // HH:MM
    end: string   // HH:MM
}

export interface ScheduleDay {
    date: string // YYYY-MM-DD
    slots: ScheduleSlot[]
    has_blocked: boolean
    state: ScheduleState
}

export interface SpaceSchedule {
    days: ScheduleDay[]
    /** GCal 綁著且健康。純粹是信任提示（看到的更準），**不是門檻**。 */
    synced: boolean
}

const STATES: ScheduleState[] = ['open', 'partial', 'full', 'closed']

const SYMBOLS: Record<ScheduleState, string> = {
    open: '●',
    partial: '◐',
    full: '✕',
    closed: '·'
}

/**
 * 認不得的 `state`（欄位缺漏、後端加了新值）一律當 `closed`。
 *
 * 落回 `closed` 而不是 `open`：`closed` 只說「未開放／未公告」，是四態裡唯一
 * 不替場主宣告任何事的一態。落回 `open` 會讓一次壞掉的回應長得像整月都訂得到。
 */
export function dayState(day?: ScheduleDay | null): ScheduleState {
    if (!day || !STATES.includes(day.state)) {
        return 'closed'
    }
    return day.state
}

export function stateSymbol(state: ScheduleState): string {
    return SYMBOLS[state] ?? SYMBOLS.closed
}

/**
 * 這一天能不能給「用這個時段詢問」的入口。
 *
 * `full` 不給：那天的送出必定被 `CreateInquiry` 的閘擋下（IQ0014），給了入口
 * 等於邀請租客去撞一堵牆。`closed` 也不給：沒有時段可填。
 */
export function canInquireOn(state: ScheduleState): boolean {
    return state === 'open' || state === 'partial'
}

/** 畫面上的時段字樣。 */
export function slotLabel(slot: ScheduleSlot): string {
    return `${slot.start} ~ ${slot.end}`
}

/**
 * 預填進詢問表單 `event_time` 的值。
 *
 * 用 `~` 無空白，與搜尋列預填（`seedFromSearch`）產出的形狀一致——後端的解析器
 * 兩種都吃，但同一個欄位在畫面上長兩種樣子只會讓人以為填錯了。
 */
export function slotTimeValue(slot: ScheduleSlot): string {
    return `${slot.start}~${slot.end}`
}

function pad(n: number): string {
    return String(n).padStart(2, '0')
}

export function formatDate(date: Date): string {
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

/**
 * 該月份的查詢範圍。一次一個月，最長 31 天——與端點的上限同一個數字。
 */
export function monthRange(date: Date): { startDate: string, endDate: string } {
    const year = date.getFullYear()
    const month = date.getMonth()
    return {
        startDate: formatDate(new Date(year, month, 1)),
        endDate: formatDate(new Date(year, month + 1, 0))
    }
}

/** 同月份（換月才重抓，同月內點日期不該再打一次 API）。 */
export function monthKey(date: Date): string {
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}`
}

/**
 * 換月上限：今天起算 +2 個月（沿用重寫前 `SpaceCalendar` 的既有規則）。
 * 再遠的日期場主多半也還沒公告，查了只會看到一片 `·`。
 */
export function isNextMonthDisabled(current: Date, now: Date): boolean {
    const limit = new Date(now.getFullYear(), now.getMonth() + 2, 1)
    return new Date(current.getFullYear(), current.getMonth(), 1) >= limit
}

/** 不給看過去的月份：已經過去的時段一律不可預約，翻回去只有噪音。 */
export function isPrevMonthDisabled(current: Date, now: Date): boolean {
    return new Date(current.getFullYear(), current.getMonth(), 1)
        <= new Date(now.getFullYear(), now.getMonth(), 1)
}

/** 以日期為鍵，讓日曆逐格 O(1) 取狀態。 */
export function indexByDate(days: ScheduleDay[]): Record<string, ScheduleDay> {
    const index: Record<string, ScheduleDay> = {}
    for (const day of days ?? []) {
        if (day?.date) {
            index[day.date] = day
        }
    }
    return index
}
